import { useEffect, useRef, useState } from 'react';

/**
 * Convenience hook for triggering a rerender when an element emits an event
 * e.g. aha-menu doesn't automatically rerender its content when opened
 *
 * Returns elementRef which must be passed to the element that emits the event
 * and renderKey which may be passed to child components to force rerender
 */
const useElementRefresh = (
  eventName: string,
  callback?: () => void,
  ignoreChildEvents: boolean = false
) => {
  // force rerender when the menu opens to ensure it retrieves fresh state (e.g. tool enabled),
  // as we don't want to have to mirror that into react state
  const [renderKey, setRenderKey] = useState(1);

  const elementRef = useRef<HTMLElement>();

  // save callback in a ref so it doesn't need to be wrapped in useCallback upstream
  const callbackRef = useRef(callback);

  useEffect(() => {
    const el = elementRef.current;
    const menuOpenedHandler = (event: Event) => {
      // If ignoreChildEvents is true, only handle events that originated from this element
      if (ignoreChildEvents && event.target !== el) {
        return;
      }

      setRenderKey(val => ++val);
      callbackRef.current?.();
    };

    el?.addEventListener(eventName, menuOpenedHandler);
    return function cleanup() {
      el?.removeEventListener(eventName, menuOpenedHandler);
    };
  }, [eventName, ignoreChildEvents]);

  return { renderKey, elementRef };
};

export default useElementRefresh;
